import { AppBar, Tab, Tabs, Toolbar, useTheme, useMediaQuery, Box, Button } from '@mui/material';
import DrawerComp from './DrawerComp.js';
import { Link, useLocation } from 'react-router-dom';

// Constants
const PAGES = ["Home", "About", "Services", "Contact"];

// Extracted styles for better maintainability
const styles = {
  appBar: {
    backgroundColor: '#F5F5F5',
    padding: { xs: '10px 0', sm: '20px 0', md: '30px 0' },
    paddingLeft: { xs: '10px', sm: '25px', md: '50px' },
    paddingRight: { xs: '10px', sm: '25px', md: '50px' },
    boxShadow: 'none',
    position: 'relative',
    minHeight: { xs: 'auto', sm: '160px', md: '200px' },
    marginBottom: { xs: '0', sm: '20px', md: '30px' }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: { xs: 'column', sm: 'row' },
    minHeight: { xs: 'auto', sm: '160px', md: '200px' },
    position: 'relative',
    backgroundColor: '#F5F5F5'
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: { xs: '0.5rem', sm: '0' },
    position: { sm: 'absolute' },
    left: { sm: '50%' },
    top: { sm: '50%' },
    transform: { sm: 'translate(-50%, -50%)' },
    zIndex: 1,
    backgroundColor: '#F5F5F5',
    padding: { sm: '10px 20px' },
    borderRadius: '4px'
  },
  logoLink: {
    textDecoration: 'none',
    display: 'block',
    padding: '4px',
    backgroundColor: '#F5F5F5',
    borderRadius: '4px'
  },
  logo: {
    width: { xs: 100, sm: 140, md: 180 },
    height: 'auto',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)'
    }
  },
  drawerContainer: {
    alignSelf: { xs: 'flex-end', sm: 'center' },
    position: { xs: 'absolute', sm: 'relative' },
    top: { xs: '10px', sm: 'auto' },
    right: { xs: '10px', sm: 'auto' },
    zIndex: 2
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    zIndex: 2,
    marginTop: { sm: 'auto' }
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    zIndex: 2,
    marginTop: { sm: 'auto' }
  },
  tabs: {
    '& .MuiTabs-flexContainer': {
      gap: { xs: '8px', sm: '12px', md: '16px' }
    }
  },
  tab: {
    color: 'black',
    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
    fontWeight: 500,
    textTransform: 'uppercase',
    transition: 'all 0.3s ease',
    padding: { xs: '4px 6px', md: '8px 12px' },
    minWidth: { xs: 'auto', md: '80px' },
    fontFamily: '"Bebas Neue", sans-serif',
    '&:hover': {
      color: '#888',
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },
    '&.Mui-selected': {
      color: '#666',
      fontWeight: 600
    }
  },
  contactButton: {
    backgroundColor: '#464d22',
    color: 'white',
    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
    fontWeight: 500,
    textTransform: 'uppercase',
    padding: { xs: '4px 6px', md: '8px 12px' },
    minWidth: { xs: 'auto', md: '80px' },
    borderRadius: '20px',
    fontFamily: '"Bebas Neue", sans-serif',
    '&:hover': {
      backgroundColor: '#757a5c',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    transition: 'all 0.3s ease-in-out',
  }
};

/**
 * Header component that displays the navigation bar with logo and menu items
 * @returns {JSX.Element} The rendered Header component
 */
const Header = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMatch = useMediaQuery(theme.breakpoints.down('md'));

  // Get current path for active tab
  const currentPath = location.pathname === '/' ? 'Home' : location.pathname.slice(1).charAt(0).toUpperCase() + location.pathname.slice(2);

  return (
    <AppBar
      position="static"
      sx={styles.appBar}
      component="header"
      role="banner"
    >
      <Toolbar
        sx={styles.toolbar}
        component="nav"
        role="navigation"
        aria-label="Main navigation"
      >
        <Box sx={styles.logoContainer}>
          <Link
            to="/"
            style={styles.logoLink}
            aria-label="Go to home page"
          >
            <Box
              component="img"
              src="/logo.jpg"
              alt="Nicole Hahn Design Logo"
              loading="eager"
              sx={styles.logo}
            />
          </Link>
        </Box>

        {isMatch ? (
          <Box sx={styles.drawerContainer}>
            <DrawerComp />
          </Box>
        ) : (
          <>
            <Box sx={styles.leftSection}>
              <Button
                component={Link}
                to="/contact"
                sx={styles.contactButton}
                aria-label="Go to contact page"
              >
                Contact
              </Button>
            </Box>
            <Box sx={styles.rightSection}>
              <Tabs
                value={currentPath}
                textColor="inherit"
                indicatorColor="secondary"
                sx={{
                  ...styles.tabs,
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#808000',
                    height: '3px'
                  }
                }}
                aria-label="Main navigation tabs"
              >
                {PAGES.filter(page => page !== "Contact").map((page) => (
                  <Tab
                    key={page}
                    label={page}
                    value={page}
                    component={Link}
                    to={page === "Home" ? "/" : `/${page.toLowerCase()}`}
                    sx={styles.tab}
                    aria-label={`Go to ${page} page`}
                  />
                ))}
              </Tabs>
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;